import React from "react";
import { useTranslation } from "react-i18next";

import { Marker as MapMarker, Popup } from "react-map-gl/mapbox";
import { Box, Typography } from "@mui/material";

import { RISK_MESSAGE_COLOR_MAP } from "../../constants";
import { getRiskMessageLabel, getRiskNameLabel } from "../../helpers";
import StreetViewAction from "../../shared/StreetViewAction";
import SegmentedRiskGauge from "../../shared/SegmentedRiskGauge/SegmentedRiskGauge";

import styles from "./marker.module.css";

const getTitle = (markerInfo) => {
  if (markerInfo.address) return markerInfo.address;
  if (markerInfo.riskData.egsa87x && markerInfo.riskData.egsa87y)
    return `${markerInfo.riskData.egsa87x}, ${markerInfo.riskData.egsa87y}`;
  if (markerInfo.riskData.latitude && markerInfo.riskData.longitude)
    return `${markerInfo.riskData.latitude}, ${markerInfo.riskData.longitude}`;
  return "-";
};

const RiskItem = ({ name, risk, message, binCount, t }) => {
  const color = RISK_MESSAGE_COLOR_MAP[message];

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection="column"
      gap={2}
      textAlign="center"
    >
      <Box>
        <Typography variant="body1">{getRiskNameLabel(name, t)}</Typography>

        <Typography
          variant="body1"
          style={{ color }}
          fontWeight={600}
          marginTop={1}
        >
          {getRiskMessageLabel(message, t)}
        </Typography>
      </Box>

      <SegmentedRiskGauge
        risk={risk}
        message={message}
        binCount={binCount}
        size={80}
        thickness={8}
        labelVariant="body1"
      />
    </Box>
  );
};

const Marker = ({ markerInfo, setMarkerInfo, showPoint = true }) => {
  const { t } = useTranslation();

  return (
    <>
      {showPoint && (
        // DOM marker (HTML overlay) so it always renders above 3D buildings —
        // a `circle` map layer would be depth-occluded by buildings when pitched.
        <MapMarker
          longitude={markerInfo.riskData.longitude}
          latitude={markerInfo.riskData.latitude}
          anchor="center"
          onClick={(e) => {
            e.originalEvent.stopPropagation();
            setMarkerInfo({ ...markerInfo, open: !markerInfo.open });
          }}
        >
          <div className={styles.marker} />
        </MapMarker>
      )}

      {markerInfo.open && (
        <Popup
          longitude={markerInfo.riskData.longitude}
          latitude={markerInfo.riskData.latitude}
          closeOnClick={false}
          onClose={() => setMarkerInfo({ ...markerInfo, open: false })}
          anchor="bottom"
          className={styles.popup}
          maxWidth="600px"
        >
          <Box textAlign="center" sx={{ backgroundColor: "white" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <StreetViewAction
                latitude={markerInfo.riskData.latitude}
                longitude={markerInfo.riskData.longitude}
              />

              <Typography variant="h6">{getTitle(markerInfo)}</Typography>
            </Box>

            <Box display="flex" gap={5} justifyContent="center">
              <RiskItem
                name="Fire"
                risk={markerInfo.riskData.fire.risk}
                message={markerInfo.riskData.fire.message}
                binCount={markerInfo.riskData.fire.description_dict?.bin_count}
                t={t}
              />
              <RiskItem
                name="Flood"
                risk={markerInfo.riskData.flood.risk}
                message={markerInfo.riskData.flood.message}
                binCount={markerInfo.riskData.flood.description_dict?.bin_count}
                t={t}
              />
              <RiskItem
                name="Earthquake"
                risk={markerInfo.riskData.earthquake.risk}
                message={markerInfo.riskData.earthquake.message}
                binCount={
                  markerInfo.riskData.earthquake.description_dict?.bin_count
                }
                t={t}
              />
            </Box>
          </Box>
        </Popup>
      )}
    </>
  );
};

export default Marker;
